import { findCalculationModuleById } from '@/config/calculationModules';
import { readAdaptiveRtDraft } from '@/utils/adaptiveRtDraftStorage';
import { readCalculationSession } from '@/utils/calculationSessionStorage';
import { normalizeModuleId } from '@/utils/adaptiveRtModule';
import { normalizeCourseId, courseIdsEqual } from '@/utils/courseId';
import { useOpenIntentStore } from '@/stores/openIntentStore';

function canonicalModeId(rawMode) {
  if (!rawMode) return null;
  return findCalculationModuleById(normalizeModuleId(rawMode))?.id ?? null;
}

function modeFromOpenIntent(courseId) {
  const intent = useOpenIntentStore().intent;
  if (!intent?.calculationModuleId) return null;
  if (!courseIdsEqual(intent.courseId, courseId)) return null;
  return canonicalModeId(intent.calculationModuleId);
}

function modeFromCalculationSession(courseId) {
  const session = readCalculationSession();
  if (!session?.mode) return null;
  const sessionCourseId = normalizeCourseId(session?.openIntent?.courseId);
  if (!courseIdsEqual(sessionCourseId, courseId)) return null;
  return canonicalModeId(session.mode);
}

/**
 * Модуль расчёта, который можно продолжить для текущего курса.
 * Не берём activeCalculationModuleId: это состояние UI, а не факт начатой работы
 * (модуль выбирают до курса, поэтому он может быть задан ещё до расчёта).
 * Приоритет: latest_mode курса (API) → openIntent этого курса → sessionStorage → черновик AdaptiveRT.
 * @param {{ selectedCourseId?: number|null, selectedCourse?: { latest_mode?: string|null }|null }} courseStore
 * @returns {string|null}
 */
export function resolveResumableCalculationModuleId(courseStore) {
  const courseId = normalizeCourseId(courseStore?.selectedCourseId);
  if (courseId == null) return null;

  const fromCourse = canonicalModeId(courseStore?.selectedCourse?.latest_mode);
  if (fromCourse) return fromCourse;

  const fromIntent = modeFromOpenIntent(courseId);
  if (fromIntent) return fromIntent;

  const fromWorkspaceSession = modeFromCalculationSession(courseId);
  if (fromWorkspaceSession) return fromWorkspaceSession;

  if (readAdaptiveRtDraft(courseId)) {
    return 'adaptive_rt';
  }

  return null;
}

/**
 * @param {string|null|undefined} left
 * @param {string|null|undefined} right
 */
export function calculationModulesEqual(left, right) {
  if (!left || !right) return false;
  return normalizeModuleId(left) === normalizeModuleId(right);
}
